import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Stack from '@mui/material/Stack'
import { CommentInput } from './components/CommentInput'
import { useCommentsQueries } from './useCommentsQueries'
import { useCommentsStyles } from './Comments.styles'
import { CommentList } from './components/CommentList'
import usePrevious from '../../hooks/usePrevious'
import { CommentsProps } from './Comments.types'
import clsx from 'clsx'
import { Toggle } from '../Toggle/Toggle'

export const Comments = ({ dataId, dataType, minimized = false, onCommentPosted, onCommentRemoved }: CommentsProps) => {
    const styles = useCommentsStyles()
    const commentsListContainerRef = useRef<HTMLDivElement>(null)

    const [inputValue, setInputValue] = useState('')
    const [showResolved, setShowResolved] = useState(false)

    const commentRemovingQueue = useRef<Record<string, boolean>>({})
    const commentUpdatingQueue = useRef<Record<string, boolean>>({})

    const addCommentToRemovingQueue = (commentId: string) => {
        commentRemovingQueue.current[commentId] = true
    }

    const deleteCommentFromRemovingQueue = (commentId: string) => {
        delete commentRemovingQueue.current[commentId]
        onCommentRemoved?.()
    }

    const addCommentToUpdatingQueue = (commentId: string) => {
        commentUpdatingQueue.current[commentId] = true
    }

    const deleteCommentFromUpdatingQueue = (commentId: string) => {
        delete commentUpdatingQueue.current[commentId]
    }

    const checkIfCommentIsInRemovingQueue = (commentId: string) => {
        return commentRemovingQueue.current[commentId] || commentUpdatingQueue.current[commentId]
    }

    const checkIfCommentIsInRemovingQueueOnly = (commentId: string) => {
        return commentRemovingQueue.current[commentId]
    }

    const clearInputValue = () => {
        setInputValue('')
    }

    const onPostComment = () => {
        clearInputValue()
        scrollToTheCommentListBottom()
        onCommentPosted?.()
    }

    const { getCommentsQuery, postCommentMutation, removeCommentMutation, updateCommentMutation } = useCommentsQueries({
        dataId,
        dataType,
        onPostComment,
        onRemoveComment: deleteCommentFromRemovingQueue,
        onUpdateComment: deleteCommentFromUpdatingQueue,
    })
    const previousCommentsData = usePrevious(getCommentsQuery.data)

    const numberOfResolvedComments = useMemo(
        () => (getCommentsQuery.data || []).filter((comment) => !!comment.isResolved).length,
        [getCommentsQuery.data]
    )

    const visibleComments = useMemo(
        () => (getCommentsQuery.data || []).filter((comment) => showResolved || !comment.isResolved),
        [getCommentsQuery.data, showResolved]
    )

    const scrollToTheCommentListBottom = (behavior: ScrollBehavior = 'smooth') => {
        setTimeout(
            () =>
                commentsListContainerRef?.current.scrollTo({
                    top: commentsListContainerRef?.current.scrollHeight,
                    behavior,
                }),
            0
        )
    }

    useEffect(() => {
        const isInitialFetch = !previousCommentsData && !!getCommentsQuery.data
        if (isInitialFetch) {
            scrollToTheCommentListBottom('auto')
        }
    }, [getCommentsQuery.data, previousCommentsData])

    const handleAddComment = () => {
        postCommentMutation.mutate(inputValue)
    }

    const handleRemoveComment = async (commentId: string) => {
        addCommentToRemovingQueue(commentId)
        await removeCommentMutation.mutateAsync(commentId)
    }

    const handleUpdateComment = async (commentId: string, updates: { action?: string; isResolved?: boolean }) => {
        addCommentToUpdatingQueue(commentId)
        await updateCommentMutation.mutateAsync({ commentId, ...updates })
    }

    const handleShowResolvedChange = useCallback((_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setShowResolved(checked)
    }, [])

    return (
        <Stack
            className={clsx(styles.container, minimized && styles.minimizedContainer)}
            position="relative"
            overflow="hidden"
            height="100%"
            width="100%"
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent={minimized ? 'flex-end' : 'space-between'}
                className={clsx(styles.header, minimized && styles.minimizedHeader)}
            >
                {!minimized && (
                    <Typography
                        className={styles.title}
                        textAlign="center"
                        // @ts-expect-error theme is not typed properly
                        variant="title3"
                    >
                        Comments
                    </Typography>
                )}
                {numberOfResolvedComments > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        className={styles.toggleWrapper}
                        gap={0.75}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Toggle checked={showResolved} onChange={handleShowResolvedChange} sx={{ m: 0 }} />
                        <Typography
                            variant="14x500"
                            onClick={() => handleShowResolvedChange(null, !showResolved)}
                            sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                color: showResolved ? 'figma.grayscale.textHier1' : 'figma.grayscale.textHier2',
                                '&:hover': { opacity: 0.8 },
                            }}
                        >
                            Show resolved ({numberOfResolvedComments})
                        </Typography>
                    </Stack>
                )}
            </Stack>

            <Stack
                ref={commentsListContainerRef}
                flex={1}
                overflow="auto"
                className={clsx(styles.commentList, minimized && styles.commentListMinimized)}
            >
                <CommentList
                    comments={visibleComments}
                    isLoading={getCommentsQuery.isLoading}
                    onRemoveComment={handleRemoveComment}
                    onUpdateComment={handleUpdateComment}
                    checkIfCommentIsLoading={checkIfCommentIsInRemovingQueue}
                    checkIfCommentIsRemoving={checkIfCommentIsInRemovingQueueOnly}
                    minimized={minimized}
                />
            </Stack>

            <Stack p={2} pt={0}>
                <CommentInput
                    value={inputValue}
                    onChange={setInputValue}
                    onSubmit={handleAddComment}
                    isLoading={getCommentsQuery.isLoading || postCommentMutation.isLoading}
                />
            </Stack>
        </Stack>
    )
}
